import React, { useId } from "react";

type FieldProps = {
  /** Label rendered above the control. */
  label?: React.ReactNode;
  /** One-line helper rendered under the control in muted text. */
  helper?: React.ReactNode;
  /** Error message rendered under the control in error tone. Replaces helper. */
  error?: React.ReactNode;
  /** Mark the field optional with " (optional)" appended to the label. */
  optional?: boolean;
  /** Single child — the control element (Input, Select, native input, …). */
  children: React.ReactElement;
  className?: string;
  /** When provided, used as the `htmlFor` on the label. Otherwise we generate one
   *  and inject it as `id` on the child if the child doesn't already have one. */
  htmlFor?: string;
};

/**
 * Form field wrapper. Stacks an eyebrow-style label, the control, and either a
 * helper line or an error line. Wires up the label's `htmlFor` to the child's
 * `id` so screen readers and click-to-focus work without consumers tracking ids.
 *
 *   <Field label="Backtest start date" helper="Defaults to 90 days ago.">
 *     <Input type="date" defaultValue="2026-01-30" />
 *   </Field>
 */
export function Field({
  label,
  helper,
  error,
  optional,
  children,
  className,
  htmlFor,
}: FieldProps) {
  const generatedId = useId();
  const childId = (children as React.ReactElement<{ id?: string }>).props?.id;
  const id = htmlFor ?? childId ?? generatedId;
  const child = childId
    ? children
    : React.cloneElement(children as React.ReactElement<{ id?: string }>, {
        id,
      });
  const cls = ["tb-field", error ? "tb-field-has-error" : "", className]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={cls}>
      {label ? (
        <label className="tb-field-label" htmlFor={id}>
          {label}
          {optional ? (
            <span className="tb-field-optional"> (optional)</span>
          ) : null}
        </label>
      ) : null}
      {child}
      {error ? (
        <div className="tb-field-error" role="alert">
          {error}
        </div>
      ) : helper ? (
        <div className="tb-field-helper">{helper}</div>
      ) : null}
    </div>
  );
}
